import React from 'react';
import { motion } from 'framer-motion';
import { HelpCircle, ChevronDown } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion';

const FAQSection = () => {
  const faqs = [
    {
      question: 'How is my data reviewed and approved?',
      answer: 'Your submission goes through a multi-step review: first our internal QA team checks for technical quality (clarity, lighting, completeness), then our partners review for usefulness and relevance to AI/robotics training. Only submissions that pass both reviews are accepted and counted toward your score.',
    },
    {
      question: 'How long does review take?',
      answer: 'Most submissions are reviewed within 3-7 business days. Complex submissions or high-volume periods may take longer. You\'ll receive an email notification once your submission has been reviewed.',
    },
    {
      question: 'Is my data private?',
      answer: 'Your personal information (name, email) is kept confidential. Video data that is accepted is used exclusively for AI and robotics training by our partners. We never sell your data to third parties. Avoid including faces or sensitive personal information in your videos.',
    },
    {
      question: 'How do I get paid?',
      answer: 'Payments are processed after partner payment confirmation. You\'ll need to provide valid payment details (bank account or e-wallet) during verification. Minimum payout threshold applies. Payments are made in PHP.',
    },
    {
      question: 'Is this guaranteed money?',
      answer: 'No. Earnings depend entirely on the quality and acceptance of your submissions. Rejected or non-compliant data receives no payment. Monthly leaderboard rewards are supplemental bonuses, not guaranteed income. Treat this as an opportunity, not a job.',
    },
    {
      question: 'How do we make money?',
      answer: 'We partner with AI and robotics companies who need high-quality training data. They pay us for curated, verified datasets that meet their specifications. A portion of that payment goes directly to you, our data contributors. The better the data quality, the more valuable it is to our partners—and the more you earn.',
    },
    {
      question: 'Can I join multiple Side Quests?',
      answer: 'Yes! You can participate in as many Side Quests as you qualify for. Each quest has its own leaderboard, so you can compete across multiple categories and maximize your earning potential.',
    },
    {
      question: 'What gets rejected most often?',
      answer: 'Common rejection reasons include: poor lighting, shaky/unstable footage, incomplete tasks (not showing full process), faces visible in frame, private information visible, heavy editing or cuts that obscure the natural flow, and data that doesn\'t match the quest requirements.',
    },
  ];

  return (
    <section id="faq" className="py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-3 mb-4">
            <HelpCircle className="w-6 h-6 text-lavender" />
            <h2 className="font-pixel text-lg sm:text-xl md:text-2xl text-foreground">
              FAQ
            </h2>
            <HelpCircle className="w-6 h-6 text-lavender" />
          </div>
          <p className="font-terminal text-xl text-lavender">
            Got questions? We&apos;ve got answers.
          </p>
        </motion.div>

        {/* FAQ Accordion */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="arcade-panel rounded-lg overflow-hidden"
        >
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="border-b-2 border-panel-purple last:border-b-0"
              >
                <AccordionTrigger className="px-6 py-5 hover:bg-neon-pink/5 transition-colors [&[data-state=open]]:bg-neon-pink/10 group">
                  <span className="font-pixel text-[10px] sm:text-xs text-left text-foreground group-hover:text-neon-pink transition-colors pr-4">
                    {faq.question}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-5">
                  <p className="font-terminal text-lg text-lavender leading-relaxed">
                    {faq.answer}
                  </p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>

        {/* Additional Help */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mt-10"
        >
          <p className="font-terminal text-lg text-muted">
            Still have questions? Reach out to us through the <a href="#submit" className="text-neon-pink hover:underline">submission form</a>.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQSection;
